/**
 * ArrayItemDialog
 * Dialog for adding or editing a single array item.
 * Dynamically renders fields based on arrayItemSchema.
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ImageUploadField } from '@/components/ui/ImageUploadField'
import { Slider } from '@/components/ui/slider'
import type { FieldConfig } from '@limo/shared'

interface ArrayItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  schema: FieldConfig[]
  item: Record<string, unknown> | null // null for new item
  onSave: (item: Record<string, unknown>) => void
  accountId: string
  itemLabel?: string // e.g., "Route", "Feature", "Service"
}

export function ArrayItemDialog({
  open,
  onOpenChange,
  schema,
  item,
  onSave,
  accountId,
  itemLabel = 'Item',
}: ArrayItemDialogProps) {
  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const isEditing = item !== null

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open) {
      if (item) {
        setFormData({ ...item })
      } else {
        // Initialize with defaults from schema
        const defaults = schema.reduce(
          (acc, field) => {
            acc[field.key] = field.defaultValue ?? ''
            return acc
          },
          {} as Record<string, unknown>
        )
        setFormData(defaults)
      }
    }
  }, [open, item, schema])

  const handleSave = () => {
    onSave(formData)
    onOpenChange(false)
  }

  const updateField = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? `Edit ${itemLabel}` : `Add ${itemLabel}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {schema.map((field) => (
            <ArrayItemFieldEditor
              key={field.key}
              field={field}
              value={formData[field.key]}
              onChange={(value) => updateField(field.key, value)}
              accountId={accountId}
            />
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>{isEditing ? 'Update' : 'Add'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Field editor for array item fields
interface ArrayItemFieldEditorProps {
  field: FieldConfig
  value: unknown
  onChange: (value: unknown) => void
  accountId: string
}

function ArrayItemFieldEditor({
  field,
  value,
  onChange,
  accountId,
}: ArrayItemFieldEditorProps) {
  const id = `array-item-field-${field.key}`

  // Helper to get value with type safety
  const getValue = <T,>(defaultValue: T): T => {
    return (value as T) ?? field.defaultValue ?? defaultValue
  }

  switch (field.type) {
    case 'text':
      return (
        <div className="space-y-2">
          <Label htmlFor={id}>{field.label}</Label>
          <Input
            id={id}
            value={getValue('')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
          />
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )

    case 'textarea':
    case 'richtext':
      return (
        <div className="space-y-2">
          <Label htmlFor={id}>{field.label}</Label>
          <Textarea
            id={id}
            value={getValue('')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            rows={3}
          />
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )

    case 'number':
      return (
        <div className="space-y-2">
          <Label htmlFor={id}>{field.label}</Label>
          <Input
            id={id}
            type="number"
            value={getValue(0)}
            onChange={(e) => onChange(Number(e.target.value))}
            placeholder={field.placeholder}
          />
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )

    case 'icon':
      return (
        <div className="space-y-2">
          <Label htmlFor={id}>{field.label}</Label>
          <Input
            id={id}
            value={getValue('')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || 'e.g., Shield, Star, Users'}
          />
          <p className="text-xs text-muted-foreground">
            {field.description ||
              'Enter a Lucide icon name (e.g., Shield, Star, Users)'}
          </p>
        </div>
      )

    case 'image':
      return (
        <ImageUploadField
          label={field.label}
          value={getValue('')}
          onChange={(url) => onChange(url)}
          accountId={accountId}
          fieldKey={`array-item-${field.key}`}
          placeholder={field.placeholder}
          description={field.description}
        />
      )

    case 'slider':
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor={id}>{field.label}</Label>
            <span className="text-sm font-medium tabular-nums">
              {getValue(field.min ?? 0)}
            </span>
          </div>
          <Slider
            id={id}
            value={[getValue(field.min ?? 0)]}
            onValueChange={(values) => onChange(values[0])}
            min={field.min ?? 0}
            max={field.max ?? 100}
            step={field.step ?? 1}
            className="w-full"
          />
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )

    default:
      return (
        <div className="space-y-2">
          <Label>{field.label}</Label>
          <Input
            value={getValue('')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
          />
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )
  }
}
